import {
  TagPickerGroup as FluentTagPickerGroup,
  TagPickerGroupProps as FluentTagPickerGroupProps,
} from '@fluentui/react-components';
import React, { KeyboardEvent, useCallback } from 'react';
import { focusTagPickerInput } from './focusTagPickerInput';

export type TagPickerGroupProps = FluentTagPickerGroupProps;

/**
 * TagPickerGroup with keyboard removal of a focused tag that keeps the caret in
 * the input.
 *
 * Fluent v9 lets the user move focus between the tags with the arrow keys and
 * removes a focused tag on Enter/Space (native button activation), but in both
 * cases focus is dropped to `<body>` afterwards. This wrapper:
 * - maps Backspace/Delete on a focused tag to the same removal (Fluent doesn't
 *   handle those keys), reusing the tag's own `onClick`/dismiss handler, and
 * - restores focus to the text input after any removal (Enter/Space/Backspace/
 *   Delete), so the user can keep typing/deleting without re-clicking.
 *
 * No extra wiring is needed beyond making the tag interactive
 * (`dismissible`/`onClick`).
 */
export const TagPickerGroup: React.FC<TagPickerGroupProps> = ({ onKeyDown, ...props }) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented) {
        return;
      }

      const isRemoveKey = event.key === 'Backspace' || event.key === 'Delete';
      const isActivateKey = event.key === 'Enter' || event.key === ' ';
      if (!isRemoveKey && !isActivateKey) {
        return;
      }

      const target = event.target as HTMLElement;
      const tag = target.closest?.('.fui-Tag');
      // Only act when the tag itself is focused (not the text input, and not a
      // child control such as the dismiss icon that handles its own key events).
      if (!tag || tag !== document.activeElement) {
        return;
      }

      const control = tag.closest('.fui-TagPickerControl');
      if (isRemoveKey) {
        // Fluent ignores these keys, so trigger the tag's own dismiss ourselves.
        event.preventDefault();
        (tag as HTMLElement).click();
      }
      // Enter/Space already remove the tag via native button activation; we only
      // need to return focus, which the removal otherwise drops to <body>.
      focusTagPickerInput(control);
    },
    [onKeyDown],
  );

  return <FluentTagPickerGroup {...props} onKeyDown={handleKeyDown} />;
};

TagPickerGroup.displayName = 'TagPickerGroup';
